import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import EmptyState from '@shared/ui/common/EmptyState';
import { useProducts } from '../../hooks/useProducts';
import { ProductFilter, ProductStatus } from '@domain/product/models/ProductFilter';
import { Product } from '@domain/product/models/Product';
import { useToast } from '@shared/hooks';
import { DeleteDialog } from '@shared/ui/dialog/DeleteDialog';
import { Icon } from '@shared/icons';

export interface ArchivedProductsBottomSheetProps {
  onClose: () => void;
}

const ArchivedProductsBottomSheet: React.FC<ArchivedProductsBottomSheetProps> = ({ onClose }) => {
  const { success, error: showError } = useToast();
  const [filterCriteria, setFilterCriteria] = useState<ProductFilter>({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const updateFilter = useCallback((criteria: ProductFilter) => {
    setFilterCriteria(criteria);
  }, []);

  const { products: productsPage, isLoading, isFetching, bulkEditProducts, bulkDeleteProducts } = useProducts(
    { page, limit, ...filterCriteria },
    ProductStatus.ARCHIVED
  );

  const products = productsPage?.status === 'success' ? productsPage.data : [];
  const totalPages = productsPage?.status === 'success' ? productsPage.totalPages ?? 0 : 0;
  const totalCount = productsPage?.status === 'success' ? productsPage.total : undefined;

  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [filterCriteria]);

  const selectedIds = useMemo(() => new Set(selectedProducts.map(p => p.id)), [selectedProducts]);

  const toggleProduct = useCallback((product: Product) => {
    setIsAllSelected(false);
    setSelectedProducts(prev => {
      if (prev.some(p => p.id === product.id)) {
        return prev.filter(p => p.id !== product.id);
      }
      return [...prev, product];
    });
  }, []);

  const allOnPageSelected =
    products.length > 0 && products.every(p => selectedIds.has(p.id));
  const someOnPageSelected = products.some(p => selectedIds.has(p.id));

  const handleSelectAllOnPage = useCallback(() => {
    if (allOnPageSelected) {
      setIsAllSelected(false);
      setSelectedProducts(prev => prev.filter(p => !products.some(pr => pr.id === p.id)));
    } else {
      setIsAllSelected(false);
      setSelectedProducts(prev => {
        const map = new Map(prev.map(p => [p.id, p]));
        products.forEach(p => map.set(p.id, p));
        return Array.from(map.values());
      });
    }
  }, [allOnPageSelected, products]);

  const handleSelectAll = useCallback(() => {
    setSelectedProducts(products);
    setIsAllSelected(true);
  }, [products]);

  const handleRestore = async () => {
    if (!isAllSelected && selectedProducts.length === 0) return;
    try {
      await bulkEditProducts({
        updateData: { active: true },
        filters: isAllSelected
          ? { ...filterCriteria, active: false }
          : { productIds: selectedProducts.map(p => p.id) },
      });
      setPage(1);
      success('Products restored');
      setSelectedProducts([]);
      setIsAllSelected(false);
    } catch {
      showError('Failed to restore products');
    }
  };

  const handleDelete = async () => {
    if (!isAllSelected && selectedProducts.length === 0) return;
    setIsDeleting(true);
    try {
      await bulkDeleteProducts(
        isAllSelected
          ? { ...filterCriteria, active: false }
          : { productIds: selectedProducts.map(p => p.id) },
      );
      setPage(1);
      success('Products deleted');
      setSelectedProducts([]);
      setIsAllSelected(false);
      setShowDeleteDialog(false);
    } catch {
      showError('Failed to delete products');
    } finally {
      setIsDeleting(false);
    }
  };

  const loading = isLoading || isFetching;

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6" component="div">
            Archived products
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <Stack
          direction="row"
          spacing={1}
          px={2.5}
          py={2}
          bgcolor="background.paper"
          borderBottom={1}
          borderColor="divider"
        >
          <Stack direction="row" alignItems="center" spacing={1} flex={1}>
            <Checkbox
              size="small"
              checked={allOnPageSelected}
              indeterminate={someOnPageSelected && !allOnPageSelected}
              onChange={handleSelectAllOnPage}
              inputProps={{ 'aria-label': 'Select all on this page' }}
            />
            <Typography variant="body2" color="text.secondary" noWrap>
              {isAllSelected ? totalCount : selectedProducts.length}/{totalCount ?? products.length}
            </Typography>
            {totalCount != null && totalCount > selectedProducts.length && !isAllSelected && (
              <Button variant="text" size="small" onClick={handleSelectAll} sx={{ whiteSpace: 'nowrap' }}>
                Select all
              </Button>
            )}
          </Stack>

          <ActiveFilters filterCriteria={filterCriteria} updateFilter={updateFilter} />

          <ProductsFilter
            filterCriteria={filterCriteria}
            updateFilter={updateFilter}
            onFilterChange={() => setPage(1)}
          />

          {(isAllSelected || selectedProducts.length > 0) && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" color="primary"  onClick={handleRestore} sx={{ flexShrink: 0 }}>
                Restore
              </Button>
              <Button
                variant="text"
                startIcon={<Icon icon="Trash01" />}
                color="error"
                onClick={() => setShowDeleteDialog(true)}
                sx={{ flexShrink: 0 }}
              >
                Delete
              </Button>
            </Stack>
          )}



        </Stack>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" py={6}>
            <CircularProgress />
          </Stack>
        ) : products.length === 0 ? (
          <EmptyState
            title="No archived products"
            description="No archived products match the current filters."
          />
        ) : (

          <List disablePadding sx={{ overflow: 'auto', flex: 1 }}>
            {products.map(product => {
              const checked = selectedIds.has(product.id);
              return (
                <ListItem key={product.id} disablePadding divider>
                  <ListItemButton
                    onClick={() => toggleProduct(product)}
                    sx={{ alignItems: 'center', gap: 2, py: 4, px: 4 }}
                  >
                    <Checkbox
                      edge="start"
                      tabIndex={-1}
                      disableRipple
                      checked={checked}
                      size="small"
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleProduct(product)}
                      inputProps={{ 'aria-label': `Select ${product.title ?? product.id}` }}
                    />
                    <ListItemAvatar sx={{ minWidth: 56 }}>
                      <Avatar
                        variant="rounded"
                        src={product.image}
                        alt=""
                        sx={{ width: 48, height: 48, borderRadius: 1, border: 1, borderColor: 'divider' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={product.title || 'Untitled'}
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        color: 'text.primary',
                        sx: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          display: '-webkit-box',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}

            {totalPages > 1 && (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} px={3} py={2} flexWrap="wrap">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="small"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
                <Select
                  value={limit}
                  label="Items per page"
                  onChange={e => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  size="small"
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </Stack>
            )}
          </List>


        )}




      </DialogContent>
      <DeleteDialog
        open={showDeleteDialog}
        cancelDelete={() => setShowDeleteDialog(false)}
        confirmDelete={handleDelete}
        isDeleting={isDeleting}
        title="Delete archived products"
        description={`Are you sure you want to delete ${isAllSelected ? totalCount : selectedProducts.length} archived product${(isAllSelected ? totalCount : selectedProducts.length) !== 1 ? 's' : ''}? This action cannot be undone.`}
      />
    </>
  );
};

export default ArchivedProductsBottomSheet;
